(() => {
  if (customElements.get('liner-color-picker')) return;
  const normalize = (value) => String(value || '').trim().toLowerCase();
  const list = (value) => String(value || '').split(',').map(normalize).filter(Boolean);
  const matches = (trigger, values) => !list(trigger).length || list(trigger).some((value) => values.includes(value));

  function options(root) {
    return window.ActiveVariantSelection.options(root);
  }

  class LinerColorPicker extends HTMLElement {
    connectedCallback() {
      if (this.choiceContainer) {
        this.root = this.closest('product-info');
        requestAnimationFrame(() => refresh(this.root));
        return;
      }
      this.root = this.closest('product-info');
      this.palettes = JSON.parse(this.querySelector('[data-color-palettes]').textContent);
      this.choiceContainer = this.querySelector('.liner-color-picker__choices');
      this.error = this.querySelector('.liner-color-picker__error');
      this.querySelector('fieldset').disabled = true;
      this.addEventListener('change', () => refresh(this.root));
      requestAnimationFrame(() => refresh(this.root));
    }

    get selected() {
      return this.querySelector('input:checked:not(:disabled)');
    }

    updateVisibility(values) {
      let visible = matches(this.dataset.triggerVariant, values);
      if (list(this.dataset.triggerPreset).length && list(this.dataset.presetStyles).some((value) => values.includes(value))) {
        const preset = Array.from(this.root.querySelectorAll('.preset-picker')).find((picker) => matches(picker.dataset.triggerVariant, values));
        visible = visible && matches(this.dataset.triggerPreset, [normalize(preset?.querySelector('input:checked')?.value)]);
      }
      this.hidden = !visible;
      this.style.display = visible ? 'block' : 'none';
      this.querySelector('fieldset').disabled = !visible;
    }

    updatePalette(variantOptions) {
      const material = variantOptions.find((option) => option.name === normalize(this.dataset.materialOption));
      const materialName = material ? material.value.toUpperCase() : 'TPU';
      if (this.material === materialName) return;
      const previous = this.querySelector('input:checked')?.value;
      this.material = materialName;
      const colors = (this.palettes[materialName] || []).filter((color) => color.name && /^#[\da-f]{6}$/i.test(color.hex));
      const seen = new Set();
      this.choiceContainer.replaceChildren();
      colors.forEach((color, index) => {
        if (seen.has(normalize(color.name))) return;
        seen.add(normalize(color.name));
        const label = document.createElement('label');
        label.className = 'liner-color-picker__choice';
        const radio = document.createElement('input');
        radio.type = 'radio';
        radio.name = `properties[${this.dataset.property}]`;
        radio.value = color.name;
        radio.id = `LinerColor-${this.dataset.blockId}-${index}`;
        radio.setAttribute('form', this.dataset.formId);
        radio.dataset.hex = color.hex;
        const swatch = document.createElement('span');
        swatch.className = 'liner-color-picker__swatch';
        swatch.style.backgroundColor = color.hex;
        swatch.setAttribute('aria-hidden', 'true');
        const name = document.createElement('span');
        name.textContent = color.name;
        label.append(radio, swatch, name);
        this.choiceContainer.append(label);
      });
      const radios = Array.from(this.querySelectorAll('input'));
      const selected = radios.find((radio) => normalize(radio.value) === normalize(previous)) || radios.find((radio) => normalize(radio.value) === normalize(this.dataset.defaultColor)) || radios[0];
      if (selected) selected.checked = true;
    }

    updateExclusion(pickers) {
      const source = pickers.find((picker) => picker !== this && !picker.hidden && normalize(picker.dataset.property) === normalize(this.dataset.excludeFrom));
      const excluded = normalize(source?.selected?.dataset.hex);
      const radios = Array.from(this.querySelectorAll('input'));
      radios.forEach((radio) => {
        radio.disabled = this.hidden || Boolean(excluded && normalize(radio.dataset.hex) === excluded);
        radio.required = !this.hidden && !radio.disabled && this.dataset.required === 'true';
        if (radio.disabled) radio.checked = false;
      });
      if (!this.selected && !this.hidden) {
        const next = radios.find((radio) => !radio.disabled && normalize(radio.value) === normalize(this.dataset.defaultColor)) || radios.find((radio) => !radio.disabled);
        if (next) next.checked = true;
      }
      const selected = this.selected;
      const preview = this.querySelector('.liner-color-picker__preview');
      preview.hidden = !selected || this.hidden;
      if (selected) {
        preview.querySelector('.liner-color-picker__large-swatch').style.backgroundColor = selected.dataset.hex;
        preview.querySelector('.liner-color-picker__name').textContent = selected.value;
      }
      this.error.hidden = this.hidden || Boolean(selected);
      this.error.textContent = radios.length ? 'No different color is available. Please change the main color.' : `No ${this.material} colors are configured for this product. Please contact us.`;
    }
  }

  function refresh(root) {
    if (!window.ActiveVariantSelection.isActive(root)) return;
    const pickers = Array.from(root.querySelectorAll('liner-color-picker')).filter((picker) => picker.choiceContainer);
    const variantOptions = options(root);
    const values = variantOptions.map((option) => option.value);
    pickers.forEach((picker) => { picker.updateVisibility(values); picker.updatePalette(variantOptions); });
    [...pickers].sort((a, b) => Boolean(a.dataset.excludeFrom) - Boolean(b.dataset.excludeFrom)).forEach((picker) => picker.updateExclusion(pickers));
    const background = pickers.find((picker) => !picker.hidden && picker.dataset.previewRole === 'background')?.selected?.dataset.hex;
    const foreground = pickers.find((picker) => !picker.hidden && picker.dataset.previewRole === 'foreground')?.selected?.dataset.hex;
    root.querySelectorAll('.product__custom-input input[type="text"]').forEach((input) => {
      if (pickers.some((picker) => picker.dataset.previewRole === 'background')) input.style.backgroundColor = background || '';
      if (pickers.some((picker) => picker.dataset.previewRole === 'foreground')) {
        input.style.color = foreground || '';
        input.style.caretColor = foreground || '';
      }
    });
  }

  customElements.define('liner-color-picker', LinerColorPicker);
  document.addEventListener('change', (event) => {
    if (event.target.closest('variant-selects, .preset-picker')) refresh(event.target.closest('product-info'));
  });
  document.addEventListener('product-info:variant-updated', (event) => refresh(event.target));
  document.addEventListener('liner-colors:refresh', (event) => {
    if (event.target.matches?.('product-info')) refresh(event.target);
    else document.querySelectorAll('product-info').forEach(refresh);
  });
  document.addEventListener('submit', (event) => {
    const root = event.target.closest('product-info');
    if (!root || !event.target.matches('product-form form')) return;
    refresh(root);
    const invalid = Array.from(root.querySelectorAll('liner-color-picker')).find((picker) => !picker.hidden && picker.dataset.required === 'true' && !picker.selected);
    if (invalid) {
      event.preventDefault();
      event.stopImmediatePropagation();
      invalid.error.hidden = false;
      invalid.scrollIntoView({ block: 'center', behavior: 'smooth' });
      invalid.error.setAttribute('tabindex', '-1');
      invalid.error.focus();
    }
  }, true);
})();